import React, { useCallback, useEffect } from "react";
import {
  Modal,
  Button,
  Badge,
  InputWrapper,
  NumberInput,
  Input,
} from "@mantine/core";
import { Limit } from "../../@types/blockedDates";
import { useCompanyLimits } from "../../providers/CompanyLimitsProvider";

export type Action = "Disable" | "Edit";

type Props = {
  isOpen: boolean;
  setIsOpen: (val: boolean) => void;
  onConfirm: (
    newValue: number,
    newState: boolean,
    intent: "enable" | "disable" | "edit"
  ) => void;
  action: string;
  limit: Limit;
  modal: {
    title: string;
    description: string;
    actionTitle: string;
    actionDescription: string;
    actionCycle: string;
  };
  limitKey: string;
};

const ConfirmUpdateCompanyLimitModal: React.FC<Props> = ({
  isOpen,
  setIsOpen,
  onConfirm,
  action,
  limit,
  modal,
  limitKey,
}) => {
  const [value, setValue] = React.useState<Limit>(limit);
  const { isLoading } = useCompanyLimits();
  const handleConfirm = useCallback(
    (intent: "enable" | "disable" | "edit") => {
      onConfirm(value.value as number, intent !== "disable", intent);
      setIsOpen(false);
    },
    [value]
  );

  useEffect(() => {
    setValue(limit);
  }, [limit]);

  return (
    <Modal
      size="lg"
      centered
      opened={isOpen}
      onClose={() => setIsOpen(false)}
      withCloseButton={false}
      classNames={{
        body: "p-8",
      }}
    >
      {action === "Disable" && (
        <div>
          <div className="flex flex-col gap-4 pb-8 border-b border-solid border-b-jem-primary-40t">
            <div className="text-base font-bold">Disable this limit?</div>
            <div className="text-sm font-normal">
              Once disabled, this limit will no longer restrict employee
              transactions.
            </div>
          </div>
          <div className="flex justify-end gap-4 pt-6">
            <Button
              variant="outline"
              color="gray"
              classNames={{
                label: "px-4 font-normal text-xs",
              }}
              onClick={() => setIsOpen(false)}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              className="px-4 font-normal"
              classNames={{
                label: "px-4 font-normal text-xs",
              }}
              onClick={() => {
                setValue((oldValue) => ({ ...oldValue, enabled: false }));
                handleConfirm("disable");
              }}
              disabled={isLoading}
            >
              {isLoading ? "Disabling..." : "Disable"}
            </Button>
          </div>
        </div>
      )}
      {action === "Edit" && (
        <div>
          <Badge
            size="sm"
            className="mb-4"
            classNames={{
              root: `${
                limit?.enabled ? "bg-jem-green-10t" : "bg-jem-background_shade"
              } capitalize h-6`,
              inner: `${
                limit?.enabled ? "text-jem-green-30s" : "text-jem-primary-80t"
              }`,
            }}
          >
            {limit?.enabled ? "Enabled" : "Disabled"}
          </Badge>
          <div className="flex flex-col gap-2 pb-6">
            <span className="text-base font-bold">{modal.title}</span>
            <span className="text-sm font-normal">{modal.description}</span>
          </div>
          <div className="flex flex-col gap-2">
            <span className="text-sm font-bold">{modal.actionTitle}</span>
            <span className="text-sm font-normal">
              {modal.actionDescription}
            </span>
            <div className="flex items-start gap-4 pb-0">
              <InputWrapper
                required
                error={
                  parseInt(value?.value?.toString() ?? "0", 10) <= 0 ||
                  Number.isNaN(parseInt(value?.value?.toString() ?? "0", 10))
                    ? "Value must be more than 0."
                    : undefined
                }
              >
                {limitKey === "request_limit" ? (
                  <NumberInput
                    min={0}
                    step={1}
                    value={
                      typeof value?.value === "string"
                        ? parseInt(value?.value, 10)
                        : (value?.value as number) ?? 0
                    }
                    onChange={(newValue) => {
                      setValue((prevValue) => ({
                        ...prevValue,
                        value: newValue ?? 0,
                      }));
                    }}
                    classNames={{
                      input: "border-1 border-jem-primary-20t",
                    }}
                  />
                ) : (
                  <Input
                    min={0}
                    step={1}
                    type="number"
                    value={
                      parseInt(value?.value?.toString() ?? "0", 10) <= 0
                        ? ""
                        : parseInt(value?.value?.toString() ?? "0", 10)
                    }
                    onChange={(e) => {
                      const inputValue = e.target.value;
                      const numericValue =
                        inputValue === "" ? 0 : parseInt(inputValue, 10);
                      setValue((prevValue) => ({
                        ...prevValue,
                        value: numericValue,
                      }));
                    }}
                    icon="R"
                    classNames={{
                      input: "border-1 border-jem-primary-20t",
                    }}
                  />
                )}
              </InputWrapper>
              <span className="pt-1">{modal.actionCycle}</span>
            </div>
          </div>
          <div className="flex justify-end gap-4 pt-8">
            <Button
              variant="outline"
              color="gray"
              classNames={{
                label: "px-4 font-normal text-xs",
              }}
              onClick={() => setIsOpen(false)}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              className="px-4 font-normal"
              classNames={{
                label: "px-4 font-normal text-xs",
              }}
              onClick={() => handleConfirm(limit?.enabled ? "edit" : "enable")}
              disabled={
                isLoading ||
                parseInt(value?.value?.toString() ?? "0", 10) <= 0 ||
                Number.isNaN(parseInt(value?.value?.toString() ?? "0", 10))
              }
            >
              {(() => {
                if (isLoading) {
                  return "Updating...";
                }
                return limit?.enabled ? "Update" : "Enable";
              })()}
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default ConfirmUpdateCompanyLimitModal;
